import { FC, useMemo } from 'react';
import { isEmpty } from 'lodash';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { LanguageType } from 'common/appSettings/appSettings.model';
import { mapSharedPropertiesToFields } from 'common/getters';
import { usePageProduct } from 'common/PageProductContext/pageProductContext';
import { isBlankPageTitle } from 'common/PageProductContext/PageTitleUtils';
import { SitecoreFieldTypes } from 'common/types/sitecore';

import { RouteFields } from './Layout';

export interface PageMetaTagsProps {
  fields: RouteFields;
  currentLanguage: string;
  publicUrl: string;
  selectedLanguages?: LanguageType[];
  selectedRegions?: LanguageType[];
}

type Regions = {
  [key: string]: string[];
};

const REGIONS: Regions = {
  'en-apac': [
    'en-apac',
    'en-bd',
    'en-kz',
    'en-bn',
    'en-la',
    'en-lk',
    'en-kh',
    'en-my',
    'en-tw',
    'en-mm',
    'en-th',
    'en-id',
    'en-pk',
    'en-vn',
    'en-ph',
    'vi-vn',
    'zh-tw',
  ],
  'en-emea': [
    'en-emea',
    'en-gr',
    'en-mt',
    'en-sk',
    'en-ma',
    'en-is',
    'en-ee',
    'en-lv',
    'en-pt',
    'en-ua',
    'en-lt',
    'en-lu',
    'el-gr',
    'hu-hu',
    'pt-pt',
  ],
  'en-mea': [
    'en-mea',
    'en-bh',
    'en-eg',
    'en-ir',
    'en-il',
    'en-ke',
    'en-lb',
    'en-sa',
    'en-ae',
    'en-zw',
    'en-ng',
    'en-na',
    'en-mz',
    'en-mu',
    'en-gh',
    'en-za',
  ],
  'nl-nl': ['nl-nl', 'nl-be'],
  'fr-fr': ['fr-fr', 'fr-be'],
  'de-de': ['de-de', 'de-at', 'de-be'],
  'fr-mea': ['fr-mea', 'fr-ma', 'fr-tn', 'fr-dz'],
  'es-lat': ['es-lat', 'es-mx', 'es-co', 'es-ar', 'es-pe', 'es-pa', 'es-cl', 'es-cr'],
};
const ISO_ENGLISH = 'en';
const LINK_REL_ALTERNATE = 'alternate';

const generateHrefLangLinks = (
  languages: LanguageType[],
  currentPath: string,
  selectedSites: LanguageType[],
  publicUrl: string
) => {
  const regionalIsoCode: keyof LanguageType = 'Regional Iso Code';
  let isEnLocalePresent = false;

  const createLink = (key: string, href: string, hrefLang: string) => (
    <link key={key} rel={LINK_REL_ALTERNATE} href={href} hrefLang={hrefLang} />
  );

  const filteredLanguages = selectedSites.length > 0 ? selectedSites : languages;

  const linkTags = filteredLanguages.flatMap((language) => {
    const languageIso = language.Iso.value.toLowerCase();
    let hrefLang = language[regionalIsoCode]?.value?.toLowerCase();

    let targetLocale = hrefLang;
    for (const [regionKey, regionLanguages] of Object.entries(REGIONS)) {
      if (regionLanguages.includes(hrefLang)) {
        targetLocale = regionKey;
        break;
      }
    }

    if (languageIso === ISO_ENGLISH && !hrefLang) {
      hrefLang = languageIso;
      isEnLocalePresent = true;
      return [
        createLink(language.id, getFullUrl(currentPath, publicUrl), hrefLang),
        createLink(`${language.id}-x-default`, getFullUrl(currentPath, publicUrl), 'x-default'),
      ];
    }

    return createLink(
      language.id,
      getFullUrl(`/${targetLocale}${currentPath}`, `${publicUrl}`),
      hrefLang
    );
  });

  if (!isEnLocalePresent && languages.length > 0) {
    const {
      id,
      [regionalIsoCode]: { value },
    } = languages[0];
    linkTags.unshift(
      createLink(
        `${id}-x-default`,
        getFullUrl(`/${value.toLowerCase()}${currentPath}`, `${publicUrl}`),
        'x-default'
      )
    );
  }

  return linkTags;
};

export const PageMetaTags: FC<PageMetaTagsProps> = ({
  fields,
  currentLanguage,
  selectedLanguages = [],
  selectedRegions = [],
  publicUrl,
}) => {
  const router = useRouter();
  const currentPath = router.asPath.split('?')[0];
  const pageProduct = usePageProduct();

  const pageFields = mapSharedPropertiesToFields<RouteFields>(fields as SitecoreFieldTypes);
  const selectedSites = pageFields?.SelectedSites;

  const hrefLinks = useMemo(
    () => generateHrefLangLinks(selectedLanguages, currentPath, selectedSites, publicUrl),
    [selectedLanguages, currentPath, selectedSites, publicUrl]
  );
  const { productName, product } = pageProduct;

  const ogImage = pageFields.OpenGraphImage;
  const pageTitle = pageFields.PageTitle?.value;

  const pageDescription = pageFields.PageDescription?.value;

  const ogImagePath = getFullUrl(pageFields.OpenGraphImage?.value.src ?? '', publicUrl);

  const pageTitleFinal = isBlankPageTitle(pageTitle) ? productName : pageTitle;

  const possibleDescriptions = [
    pageDescription,
    product?.summary,
    product?.description,
    product?.uniqueSellingPoint,
    pageTitleFinal,
  ];

  const pageDescriptionFinal = possibleDescriptions.find((description) => !isEmpty(description));

  let robotParts = [];
  if (pageFields.NoIndex?.value) {
    robotParts.push('noindex');
  }
  if (pageFields.NoFollow?.value) {
    robotParts.push('nofollow');
  }
  const robotText = robotParts.join(',');

  return (
    <Head>
      <title>{pageTitleFinal}</title>
      <meta name="description" content={pageDescriptionFinal} />
      <meta property="og:title" content={pageTitleFinal} />
      <meta property="og:description" content={pageDescriptionFinal} />
      {ogImage && (
        <>
          <meta property="og:image" content={ogImagePath} />
          <meta property="og:image:height" content={ogImage.value.height} />
          <meta property="og:image:width" content={ogImage.value.width} />
          <meta name="thumbnail" content={ogImagePath} />
        </>
      )}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Jabra" />
      <meta name="msapplication-TileColor" content="#ffffff" />
      <meta name="msapplication-TileImage" content="/favicons/jabra/ms-icon-144x144.png" />
      {!isEmpty(robotText) && <meta name="robots" content={robotText} />}

      <link
        rel="canonical"
        href={getFullUrl(
          `${currentLanguage.toLowerCase() === 'en' ? '' : `/${currentLanguage.toLowerCase()}`}${currentPath}`,
          `${publicUrl}`
        )}
      />
      <link rel="shortcut icon" href="/favicons/jabra/favicon-32x32.png" />
      <link rel="apple-touch-icon" sizes="57x57" href="/favicons/jabra/apple-icon-57x57.png" />
      <link rel="apple-touch-icon" sizes="60x60" href="/favicons/jabra/apple-icon-60x60.png" />
      <link rel="apple-touch-icon" sizes="72x72" href="/favicons/jabra/apple-icon-72x72.png" />
      <link rel="apple-touch-icon" sizes="76x76" href="/favicons/jabra/apple-icon-76x76.png" />
      <link rel="apple-touch-icon" sizes="114x114" href="/favicons/jabra/apple-icon-114x114.png" />
      <link rel="apple-touch-icon" sizes="120x120" href="/favicons/jabra/apple-icon-120x120.png" />
      <link rel="apple-touch-icon" sizes="144x144" href="/favicons/jabra/apple-icon-144x144.png" />
      <link rel="apple-touch-icon" sizes="152x152" href="/favicons/jabra/apple-icon-152x152.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/favicons/jabra/apple-icon-180x180.png" />
      <link
        rel="icon"
        type="image/png"
        sizes="192x192"
        href="/favicons/jabra/android-icon-192x192.png"
      />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicons/jabra/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="96x96" href="/favicons/jabra/favicon-96x96.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicons/jabra/favicon-16x16.png" />
      {!!selectedRegions.length && (
        <>
          {selectedRegions.map((region) => (
            <meta
              key={region.id}
              name={`region-${region['Regional Iso Code']?.value?.toLowerCase()}`}
              content={getFullUrl(
                `/${region['Regional Iso Code']?.value?.toLowerCase()}${currentPath}`,
                `${publicUrl}`
              )}
            />
          ))}
        </>
      )}
      {!!hrefLinks.length && <>{hrefLinks.map((lang) => lang)}</>}
    </Head>
  );
};

const getFullUrl = (url: string, publicUrl: string) => {
  if (typeof document !== 'undefined') {
    return new URL(url, document.baseURI).href;
  }

  const baseUrl = publicUrl || 'https://www.jabra.com';
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
  return `${baseUrl}${normalizedUrl}`;
};
